import { useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Users, PlusCircle, FileSpreadsheet, RotateCw } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import {
  ODataGrid,
  type ODataGridConfig,
  type GridToolbarHelpers,
} from '@inuxe/prime-odata-grid';
import CommonButton from '../../../components/common-components/button/CommonButton';
import { useStore } from '../../../hooks/useStore';
import type { IPerson } from '../../../models/person/PersonModel';
import { toast } from 'react-toastify';
import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../utils/odataGridUtils';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import gridStyles from '../../../styles/ODataGridPage.module.css';
import { usePersonsColumns } from './Persons.grid';
import styles from './Persons.module.css';
import NewPersonPopup from './parts/NewPersonPopup';
import EditPersonPopup from './parts/EditPersonPopup';
import DeletePersonDialog from './parts/DeletePersonDialog';

const GRID_ID = 'persons-grid';

const Persons = () => {
  const { popupStore, personStore } = useStore();
  const columns = usePersonsColumns();

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IPerson>>(
    () => ({
      gridId: GRID_ID,
      rows: 20,
      oDataMapping,
      fetchRows: async params => {
        const result = await personStore.listAllPersons(buildLegacyGridParams(params, { oDataMapping }));
        return { rows: result.rows, totalRecords: result.lastRow };
      },
    }),
    [personStore, oDataMapping]
  );

  const onGridReady = useCallback(
    (helpers: GridToolbarHelpers<IPerson>) => {
      personStore.setGridHelpers(helpers);
    },
    [personStore]
  );

  const exportToExcel = useCallback(
    async (helpers: GridToolbarHelpers<IPerson>) => {
      try {
        const queryState = helpers.getQueryState({ skip: 0, top: 1000000 });
        const result = await personStore.listAllPersons(buildLegacyGridParams(queryState));

        if (!result?.rows.length) {
          toast.warning("Excel'e aktarılacak veri bulunamadı");
          return;
        }

        const exportData = result.rows.map(item => ({
          ID: item.id,
          İsim: item.name,
          Hastane: item.hospitalName,
          'Telefon Numarası': item.phoneNumber,
          'E-mail': item.email || '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Kişiler');
        XLSX.writeFile(wb, `Kisiler_${new Date().toISOString().split('T')[0]}.xlsx`);

        toast.success(`${exportData.length} kişi Excel dosyasına aktarıldı`);
      } catch (error) {
        console.error('Excel export error:', error);
        toast.error("Excel'e aktarılırken bir hata oluştu");
      }
    },
    [personStore]
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <span className={styles.titleIcon} aria-hidden="true">
              <Users size={20} strokeWidth={2} />
            </span>
            <div className={styles.titleContent}>
              <h2 className={styles.mainTitle}>Kişiler</h2>
              <p className={styles.pageDescription}>
                Kişi kayıtlarını oluşturun, düzenleyin ve yönetin.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <ODataGrid<IPerson>
            gridId={GRID_ID}
            config={gridConfig}
            columns={columns}
            dataKey="id"
            wrapperClassName={gridStyles.oDataGridPage}
            resizableColumns
            onGridReady={onGridReady}
            toolbarLeft={helpers => (
              <>
                <CommonButton
                  type="primary"
                  onClick={() => popupStore.setIsNewPersonOpen(true)}
                  leftIcon={<PlusCircle size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnPerson}
                >
                  Yeni Kişi
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={() => void exportToExcel(helpers)}
                  leftIcon={<FileSpreadsheet size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={() => helpers.refreshGrid()}
                  leftIcon={<RotateCw size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
          />
        </div>
      </div>

      {popupStore.isNewPersonOpen && <NewPersonPopup />}
      {popupStore.isEditPersonOpen && <EditPersonPopup />}
      <DeletePersonDialog />
    </>
  );
};

export default observer(Persons);
